/*
comment.js
Restores the original comment logic from feed.js with updated toggle behavior using a "hidden" class.
- Toggles open/closed by adding/removing the "hidden" class.
- Fetches comments when first opened.
- Exports handleToggleComments so that highlight.js can call it directly.
*/

export function initCommentHandlers(config) {
  // Attach a single document-level click handler to handle:
  // - "View Comments" toggling
  // - New comment posts
  // - Reply actions and submission
  document.addEventListener('click', async (event) => {
    // Toggle Comments
    if (event.target.closest('.comments-toggle') || event.target.closest('.comments-link')) {
      event.preventDefault();
      const toggleEl = event.target.closest('.comments-toggle');
      if (toggleEl) {
        await handleToggleComments(toggleEl, config);
      }
      return;
    }

    // Submit New Comment
    if (event.target.classList.contains('submit-comment')) {
      event.preventDefault();
      await handleNewComment(event.target, config);
      return;
    }

    // Reply Button
    if (event.target.closest('.reply-btn')) {
      event.preventDefault();
      await showReplyForm(event.target);
      return;
    }

    // Submit Reply
    if (event.target.classList.contains('submit-reply-btn')) {
      event.preventDefault();
      await handleSubmitReply(event.target, config);
      return;
    }
  });
}

// Track whether comments are being fetched for each rating.
const fetchingCommentsFlags = {};

/**
 * Toggle open or closed. When opening, remove the "hidden" class and fetch comments if needed.
 * When closing, add the "hidden" class.
 */
export async function handleToggleComments(toggleEl, config) {
  const ratingId = toggleEl.getAttribute('data-rating-id');
  if (!ratingId) return;

  const commentsSection = document.getElementById(`comments-${ratingId}`);
  if (!commentsSection) return;

  // Check if the section is hidden (using the "hidden" class or inline display)
  if (commentsSection.classList.contains('hidden') || commentsSection.style.display === 'none') {
    // Remove the hidden class to show the comments.
    commentsSection.classList.remove('hidden');
    commentsSection.style.display = 'block';

    // Optionally show a loading message.
    const commentList = commentsSection.querySelector('.comment-list');
    if (commentList) {
      commentList.innerHTML = '<p style="padding: 0.5rem; font-style: italic;">Loading comments...</p>';
    }

    // If a fetch for this rating's comments is already in progress, update the message and exit.
    if (fetchingCommentsFlags[ratingId]) {
      if (commentList) {
        commentList.innerHTML = '<p style="padding: 0.5rem; font-style: italic;">Still loading comments...</p>';
      }
      return;
    }
    fetchingCommentsFlags[ratingId] = true;
    await fetchAndRenderComments(ratingId, config);
    fetchingCommentsFlags[ratingId] = false;
  } else {
    // If comments are visible, hide them by adding the hidden class.
    commentsSection.classList.add('hidden');
    commentsSection.style.display = 'none';
  }
}

/**
 * Fetches and renders comments for a given rating.
 */
async function fetchAndRenderComments(ratingId, config) {
  try {
    const resp = await fetch(`/feed/${ratingId}/comments`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });
    if (!resp.ok) {
      console.error('Failed to fetch comments for rating', ratingId);
      return;
    }
    const data = await resp.json();
    const comments = data.comments || [];

    const commentsSection = document.getElementById(`comments-${ratingId}`);
    if (!commentsSection) return;

    const commentList = commentsSection.querySelector('.comment-list');
    if (!commentList) return;

    commentList.innerHTML = '';
    renderComments(comments, commentList, ratingId, config);
  } catch (error) {
    console.error('Error fetching comments:', error);
  }
}

/**
 * Recursively renders nested comments.
 */
function renderComments(commentData, container, ratingId, config, currentLevel = 1) {
  const feedContainer = document.getElementById('feed');
  const currentUserId = feedContainer ? feedContainer.getAttribute('data-current-user-id') : null;
  const isAdmin = feedContainer ? (feedContainer.getAttribute('data-is-admin') === 'true') : false;

  commentData.forEach(comment => {
    const commentEl = document.createElement('div');
    commentEl.classList.add('comment-item');
    commentEl.id = `comment-${comment.id}`;
    commentEl.setAttribute('data-comment-id', comment.id);
    commentEl.setAttribute('data-rating-id', ratingId);
    commentEl.dataset.nestLevel = currentLevel;

    // Determine ownership.
    const userIsOwner =
      (comment.user_id &&
        currentUserId &&
        comment.user_id.toString() === currentUserId.toString()) ||
      isAdmin;
    commentEl.dataset.isOwner = userIsOwner ? 'true' : 'false';

    // Create a header container that holds the collapse toggle (if needed) and the author info.
    const headerEl = document.createElement('div');
    headerEl.classList.add('comment-header');
    
    // If the comment has children, add a collapse toggle button.
    if (comment.children && comment.children.length > 0) {
      const collapseBtn = document.createElement('span');
      collapseBtn.classList.add('collapse-btn');
      collapseBtn.textContent = '[-]'; // default expanded
      collapseBtn.addEventListener('click', () => {
        commentEl.classList.toggle('collapsed');
        collapseBtn.textContent = commentEl.classList.contains('collapsed') ? '[+]' : '[-]';
      });
      headerEl.appendChild(collapseBtn);
    }
    
    // Author
    const authorEl = document.createElement('div');
    authorEl.classList.add('comment-author');
    if (!comment.user_id) {
      const deletedSpan = document.createElement('span');
      deletedSpan.textContent = '[deleted]';
      deletedSpan.style.fontWeight = 'bold';
      authorEl.appendChild(deletedSpan);
    } else {
      const authorLink = document.createElement('a');
      authorLink.href = `/@${comment.user_username}`;
      authorLink.textContent = comment.user_username;
      authorLink.style.color = '#1E90FF';
      authorLink.style.fontWeight = 'bold';
      authorEl.appendChild(authorLink);
    }
    headerEl.appendChild(authorEl);
    commentEl.appendChild(headerEl);

    // Comment text
    const textEl = document.createElement('div');
    textEl.classList.add('comment-text');
    if (!comment.user_id || comment.text === '[deleted]') {
      textEl.textContent = '[deleted]';
      textEl.style.fontStyle = 'italic';
    } else {
      textEl.textContent = comment.text;
    }
    commentEl.appendChild(textEl);

    // Actions
    const actionsEl = document.createElement('div');
    actionsEl.classList.add('comment-actions');

    // Reply Button
    const replyBtn = document.createElement('button');
    replyBtn.classList.add('reply-btn', 'btn', 'btn-link');
    replyBtn.style.padding = '4px 8px';
    replyBtn.setAttribute('data-comment-id', comment.id);
    replyBtn.setAttribute('data-rating-id', ratingId);
    replyBtn.innerHTML = `<i class="fas fa-reply"></i>`;
    replyBtn.title = 'Reply';
    actionsEl.appendChild(replyBtn);

    // 3-dot Options Button
    const commentOptionsBtn = document.createElement('button');
    commentOptionsBtn.classList.add('comment-options-btn');
    commentOptionsBtn.innerHTML = `<i class="fas fa-ellipsis-h"></i>`;
    actionsEl.appendChild(commentOptionsBtn);

    commentEl.appendChild(actionsEl);
    container.appendChild(commentEl);

    // Nested replies container
    if (comment.children && comment.children.length > 0) {
      const childContainer = document.createElement('div');
      childContainer.classList.add('comment-children', 'nested');
      childContainer.dataset.nestLevel = currentLevel + 1;
      commentEl.appendChild(childContainer);
      renderComments(comment.children, childContainer, ratingId, config, currentLevel + 1);
    }

    // 3-dot Options Event Handler (assumes global openCommentOptionsMenu exists)
    commentOptionsBtn.addEventListener('click', (e) => {
      e.preventDefault();
      if (typeof window.openCommentOptionsMenu === 'function') {
        window.openCommentOptionsMenu(comment.id, commentEl.dataset.isOwner);
      }
    });
  });
}

/**
 * Handle posting a new top-level comment.
 */
async function handleNewComment(btn, config) {
  const ratingId = btn.getAttribute('data-rating-id');
  const commentsSection = document.getElementById(`comments-${ratingId}`);
  if (!commentsSection) return;

  const textarea = commentsSection.querySelector('.new-comment-text');
  if (!textarea) return;

  const text = textarea.value.trim();
  if (!text) return;

  try {
    const resp = await fetch(`/feed/${ratingId}/comments`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': config.csrfToken,
      },
      body: JSON.stringify({ text }),
    });
    if (!resp.ok) {
      window.showToast('Failed to post comment.', 'danger');
      return;
    }
    const data = await resp.json();
    if (data.success && data.comment) {
      textarea.value = '';

      // Immediately append new comment
      const newComment = data.comment;
      const commentList = commentsSection.querySelector('.comment-list');
      if (commentList) {
        appendComment(newComment, commentList, ratingId, config);
      }
      const commentCountSpan = document.getElementById(`comment-count-${ratingId}`);
      if (commentCountSpan) {
        commentCountSpan.textContent = parseInt(commentCountSpan.textContent, 10) + 1;
      }

      // Re-fetch entire comment list so that all nested IDs are fresh.
      await fetchAndRenderComments(ratingId, config);
    } else {
      window.showToast('Failed to post comment.', 'danger');
    }
  } catch (error) {
    console.error('Error posting comment:', error);
    window.showToast('Error posting comment.', 'danger');
  }
}

/**
 * Append a new comment dynamically (e.g. after a POST).
 */
function appendComment(comment, container, ratingId, config, currentLevel = 1) {
  const feedContainer = document.getElementById('feed');
  const isAdmin = feedContainer
    ? feedContainer.getAttribute('data-is-admin') === 'true'
    : false;
  const currentUserId = feedContainer
    ? feedContainer.getAttribute('data-current-user-id')
    : '';

  const commentEl = document.createElement('div');
  commentEl.classList.add('comment-item');
  commentEl.id = `comment-${comment.id}`;
  commentEl.setAttribute('data-comment-id', comment.id);
  commentEl.setAttribute('data-rating-id', ratingId);
  commentEl.dataset.nestLevel = currentLevel;

  const userIsOwner =
    (comment.user_id &&
      currentUserId &&
      comment.user_id.toString() === currentUserId.toString()) ||
    isAdmin;
  commentEl.dataset.isOwner = userIsOwner ? 'true' : 'false';

  // Create header with collapse toggle if needed.
  const headerEl = document.createElement('div');
  headerEl.classList.add('comment-header');

  if (comment.children && comment.children.length > 0) {
    const collapseBtn = document.createElement('span');
    collapseBtn.classList.add('collapse-btn');
    collapseBtn.textContent = '[-]';
    collapseBtn.addEventListener('click', () => {
      commentEl.classList.toggle('collapsed');
      collapseBtn.textContent = commentEl.classList.contains('collapsed') ? '[+]' : '[-]';
    });
    headerEl.appendChild(collapseBtn);
  }

  // Author
  const authorEl = document.createElement('div');
  authorEl.classList.add('comment-author');
  if (!comment.user_id || comment.text === '[deleted]') {
    const delSpan = document.createElement('span');
    delSpan.textContent = '[deleted]';
    delSpan.style.fontWeight = 'bold';
    authorEl.appendChild(delSpan);
  } else {
    const authorLink = document.createElement('a');
    authorLink.href = `/@${comment.user_username}`;
    authorLink.textContent = comment.user_username;
    authorLink.style.color = '#3b82f6';
    authorLink.style.fontWeight = 'bold';
    authorEl.appendChild(authorLink);
  }
  headerEl.appendChild(authorEl);
  commentEl.appendChild(headerEl);

  // Comment text
  const textEl = document.createElement('div');
  textEl.classList.add('comment-text');
  if (!comment.user_id || comment.text === '[deleted]') {
    textEl.textContent = '[deleted]';
    textEl.style.fontStyle = 'italic';
  } else {
    textEl.textContent = comment.text;
  }
  commentEl.appendChild(textEl);

  // Actions
  const actionsEl = document.createElement('div');
  actionsEl.classList.add('comment-actions');

  const replyBtn = document.createElement('button');
  replyBtn.classList.add('reply-btn', 'btn', 'btn-link', 'p-0', 'mr-2');
  replyBtn.setAttribute('data-comment-id', comment.id);
  replyBtn.setAttribute('data-rating-id', ratingId);
  replyBtn.innerHTML = `<i class="fas fa-reply"></i>`;
  actionsEl.appendChild(replyBtn);

  const commentOptionsBtn = document.createElement('button');
  commentOptionsBtn.classList.add('comment-options-btn');
  commentOptionsBtn.innerHTML = `<i class="fas fa-ellipsis-h"></i>`;
  actionsEl.appendChild(commentOptionsBtn);

  commentEl.appendChild(actionsEl);
  container.appendChild(commentEl);

  // Nested replies container
  if (comment.children && comment.children.length > 0) {
    const childContainer = document.createElement('div');
    childContainer.classList.add('comment-children', 'nested');
    childContainer.dataset.nestLevel = currentLevel + 1;
    commentEl.appendChild(childContainer);

    comment.children.forEach((child) => {
      appendComment(child, childContainer, ratingId, config, currentLevel + 1);
    });
  }

  commentOptionsBtn.addEventListener('click', (e) => {
    e.preventDefault();
    if (typeof window.openCommentOptionsMenu === 'function') {
      window.openCommentOptionsMenu(comment.id, commentEl.dataset.isOwner);
    }
  });
}

/**
 * Show or hide the reply form for a given comment.
 */
export async function showReplyForm(replyBtn) {
  const parentCommentId = replyBtn.getAttribute('data-comment-id');
  if (!parentCommentId || parentCommentId === "null") {
    console.error("No valid parent comment ID found.");
    return;
  }
  
  // Try to get ratingId from the button, and if missing, from the parent comment element.
  let ratingId = replyBtn.getAttribute('data-rating-id');
  if (!ratingId || ratingId === "null") {
    const commentItem = replyBtn.closest('.comment-item');
    ratingId = commentItem ? commentItem.getAttribute('data-rating-id') : null;
  }
  if (!ratingId || ratingId === "null") {
    console.error("No valid rating ID found for reply.");
    return;
  }

  const commentItem = replyBtn.closest('.comment-item');
  if (!commentItem) return;

  let existingForm = commentItem.querySelector('.reply-form');
  if (existingForm) {
    existingForm.style.display = (existingForm.style.display === 'none' ? 'block' : 'none');
    return;
  }
  existingForm = document.createElement('div');
  existingForm.classList.add('reply-form');
  existingForm.style.marginTop = '0.5rem';
  existingForm.innerHTML = `
      <textarea class="new-comment-text reply-textarea" rows="2" placeholder="Write a reply..."></textarea>
      <button class="btn btn-sm btn-primary submit-reply-btn" data-rating-id="${ratingId}" data-parent-id="${parentCommentId}">
        Reply
      </button>
    `;
  commentItem.appendChild(existingForm);
}

/**
 * Handle posting a reply.
 */
export async function handleSubmitReply(submitBtn, config) {
  const ratingId = submitBtn.getAttribute('data-rating-id');
  const parentCommentId = submitBtn.getAttribute('data-parent-id');
  const commentItem = submitBtn.closest('.comment-item');
  if (!commentItem) return;

  const replyForm = commentItem.querySelector('.reply-form');
  if (!replyForm) return;

  const replyText = replyForm.querySelector('.reply-textarea');
  if (!replyText) return;

  const text = replyText.value.trim();
  if (!text) return;

  try {
    const resp = await fetch(`/feed/${ratingId}/comments`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': config.csrfToken,
      },
      body: JSON.stringify({ text, parentCommentId }),
    });
    if (!resp.ok) {
      window.showToast('Failed to post reply.', 'danger');
      return;
    }
    const data = await resp.json();
    if (data.success && data.comment) {
      replyText.value = '';
      replyForm.style.display = 'none';

      const newComment = data.comment;
      let childContainer = commentItem.querySelector('.comment-children.nested');
      if (!childContainer) {
        childContainer = document.createElement('div');
        childContainer.classList.add('comment-children', 'nested');
        commentItem.appendChild(childContainer);
      }
      appendComment(
        newComment,
        childContainer,
        ratingId,
        config,
        (parseInt(commentItem.dataset.nestLevel, 10) || 1) + 1
      );
      const commentCountSpan = document.getElementById(`comment-count-${ratingId}`);
      if (commentCountSpan) {
        const oldVal = parseInt(commentCountSpan.textContent, 10);
        commentCountSpan.textContent = oldVal + 1;
      }

      // Re-fetch entire comment list so that all nested replies are updated.
      await fetchAndRenderComments(ratingId, config);
    }
  } catch (error) {
    console.error('Error posting reply:', error);
    window.showToast('Error posting reply.', 'danger');
  }
}
